import clsx from 'clsx'
import React, {useRef, useState} from 'react'

import {LinkButton} from '../linkbutton'
import {Grid} from '../grid'
import {PlusIcon} from '../icons'
import {Image} from '../image'
import {Link} from '../link'
import {Paper} from '../paper'
import {Popper} from '../popper'
import {Typography} from '../typography'
import {classes, listClasses} from './AutorizedUser.classes'
import {Account} from './types'
import UserItem from './UserItem'
import {toAvatarSources, toPassportUrls} from './utils'

type Props = {
  account: Account
  accounts: Account[]
  passportHost: string
  avatarHost: string
  returnPath: string
  yui: string
  canAddMore: boolean
  addUserText: string
  signOutText: string
  mobile?: boolean
  avatarClassName?: string

  showUsername?: boolean
}

const popperOffset: [number, number] = [10, 35]

const AuthorizedUser: React.FC<Props> = props => {
  const {
    account,
    accounts,
    canAddMore,
    addUserText,
    signOutText,
    avatarHost,
    passportHost,
    returnPath,
    yui,
    avatarClassName,
    mobile = false,
    showUsername = false,
  } = props
  const {
    displayName: {name, default_avatar: defaultAvatar},
  } = account
  const popperTargetRef = useRef<HTMLAnchorElement>(null)
  const [isOpened, setIsOpened] = useState(false)
  const toggleHandler = () => {
    setIsOpened(opened => !opened)
  }
  const closeHandler = () => {
    setIsOpened(false)
  }
  const passportUrls = toPassportUrls(passportHost, returnPath, yui)

  return (
    <>
      <Link
        ref={popperTargetRef}
        aria-label={name}
        className={mobile ? classes.link : undefined}
        href={passportUrls.profile}
        onPress={toggleHandler}
        preventDefault
      >
        <Grid alignItems="center" wrap="nowrap" container>
          {showUsername && (
            <Grid item>
              <Typography className={classes.username} group="tight" thickness="regular" variant="body1">
                {name}
              </Typography>
            </Grid>
          )}
          <Grid item>
            <Image
              alt={name}
              className={clsx(classes.avatar, avatarClassName)}
              height={32}
              srcSet={toAvatarSources(avatarHost, defaultAvatar)}
              width={32}
            />
          </Grid>
        </Grid>
      </Link>
      <Popper
        offset={popperOffset}
        onClose={closeHandler}
        opened={isOpened}
        placement="bottom-end"
        target={popperTargetRef}
      >
        <Paper flexDirection="column">
          <div className={classes.header}>
            <UserItem
              account={account}
              avatarUrl={avatarHost}
              cameraUrl={passportUrls.camera}
              passportHost={passportHost}
              returnPath={returnPath}
              yui={yui}
            />
          </div>
          {accounts.length > 0 && (
            <ul className={listClasses.root}>
              {accounts.map(account => (
                <UserItem
                  key={account.defaultEmail}
                  account={account}
                  avatarUrl={avatarHost}
                  passportHost={passportHost}
                  returnPath={returnPath}
                  yui={yui}
                />
              ))}
            </ul>
          )}
          <div className={classes.footer}>
            <Grid size="s" container>
              {canAddMore && (
                <Grid xs={12} item>
                  <LinkButton
                    className={classes.noWrap}
                    href={passportUrls.signIn}
                    size="m"
                    variant="outline"
                    fullWidth
                  >
                    <Grid alignItems="center" justify="center" container>
                      <PlusIcon />
                      <Typography ml="s" variant="body2">
                        {addUserText}
                      </Typography>
                    </Grid>
                  </LinkButton>
                </Grid>
              )}
              <Grid xs={12} item>
                <LinkButton href={passportUrls.signOut} size="s" variant="ghost" fullWidth>
                  {signOutText}
                </LinkButton>
              </Grid>
            </Grid>
          </div>
        </Paper>
      </Popper>
    </>
  )
}

export default AuthorizedUser
